import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { 
  ArrowLeft, 
  Clock, 
  Target, 
  Flame, 
  Calendar,
  TrendingUp,
  Award,
  Heart,
  Zap,
  Moon,
  Settings,
  ChevronRight,
  Leaf,
  Mountain,
  Sun,
  Sparkles,
  Music,
  Brain,
  Check,
  Share2
} from "lucide-react";
import axios from "axios";
import { toast } from "sonner";
import { Button } from "../components/ui/button";
import { Card } from "../components/ui/card";
import { AchievementModal, AchievementBadge } from "../components/AchievementBadge";

const API_URL = process.env.REACT_APP_BACKEND_URL;

const getToken = () => localStorage.getItem("reclaim_token");

// Mode icons
const MODE_ICONS = {
  "Calm / Meditation": Heart,
  "Performance": Zap,
  "Deep Recovery": Moon
};

// Milestone icons
const MILESTONE_ICONS = {
  "seedling": Leaf,
  "leaf": Leaf,
  "tree": Mountain,
  "mountain": Mountain,
  "sun": Sun
};

const PrivateSuiteStatsPage = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState(null);
  const [showGoalEditor, setShowGoalEditor] = useState(false);
  const [goals, setGoals] = useState({
    weekly_minutes_goal: 60,
    weekly_sessions_goal: 5,
    streak_goal: 7
  });
  const [savingGoals, setSavingGoals] = useState(false);
  
  // Achievement modal state
  const [selectedMilestone, setSelectedMilestone] = useState(null);
  const [showAchievementModal, setShowAchievementModal] = useState(false);

  useEffect(() => {
    fetchStats();
  }, []);

  const fetchStats = async () => {
    try {
      const response = await axios.get(
        `${API_URL}/api/private-suite/stats/dashboard`,
        { headers: { Authorization: `Bearer ${getToken()}` } }
      );
      setStats(response.data);
      if (response.data.goals) {
        setGoals(response.data.goals);
      }
    } catch (err) {
      console.error("Error fetching stats:", err);
      toast.error("Failed to load statistics");
    } finally {
      setLoading(false);
    }
  };

  const saveGoals = async () => {
    setSavingGoals(true);
    try {
      await axios.put(
        `${API_URL}/api/private-suite/stats/goals`,
        goals,
        { headers: { Authorization: `Bearer ${getToken()}` } }
      );
      toast.success("Goals updated");
      setShowGoalEditor(false);
      fetchStats();
    } catch (err) {
      console.error("Error saving goals:", err);
      toast.error("Failed to save goals");
    } finally {
      setSavingGoals(false);
    }
  };

  const formatMinutes = (mins) => {
    if (mins >= 60) {
      const hours = Math.floor(mins / 60);
      const remaining = Math.round(mins % 60);
      return remaining > 0 ? `${hours}h ${remaining}m` : `${hours}h`;
    }
    return `${Math.round(mins)}m`;
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return "";
    const date = new Date(dateStr);
    return date.toLocaleDateString("en-US", { month: "short", day: "numeric" });
  };

  const getRelativeTime = (dateStr) => {
    if (!dateStr) return "";
    const date = new Date(dateStr);
    const now = new Date();
    const diffMs = now - date;
    const diffHours = Math.floor(diffMs / (1000 * 60 * 60));
    const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));
    
    if (diffHours < 1) return "Just now";
    if (diffHours < 24) return `${diffHours}h ago`;
    if (diffDays === 1) return "Yesterday";
    if (diffDays < 7) return `${diffDays} days ago`;
    return formatDate(dateStr);
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-b from-[#0f0f1a] to-[#1a1a2e] flex items-center justify-center">
        <div className="w-8 h-8 border-2 border-violet-400 border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  if (!stats?.has_data) {
    return (
      <div className="min-h-screen bg-gradient-to-b from-[#0f0f1a] to-[#1a1a2e] text-white">
        <div className="p-6">
          <button onClick={() => navigate("/private-suite")} className="text-white/60 hover:text-white mb-8">
            <ArrowLeft size={24} />
          </button>
          
          <div className="text-center py-20">
            <div className="w-20 h-20 bg-violet-500/20 rounded-full flex items-center justify-center mx-auto mb-6">
              <Sparkles size={32} className="text-violet-400" />
            </div>
            <h2 className="text-2xl font-heading text-white mb-3">Your Journey Awaits</h2>
            <p className="text-white/50 max-w-xs mx-auto mb-8">
              Complete your first session to start tracking your progress
            </p>
            <Button
              onClick={() => navigate("/private-suite")}
              className="bg-white text-[#1a1a2e] px-8 py-3 rounded-full"
            >
              Begin First Session
            </Button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-b from-[#0f0f1a] to-[#1a1a2e] text-white pb-24">
      {/* Header */}
      <div className="p-6 pb-4">
        <div className="flex items-center justify-between mb-6">
          <button onClick={() => navigate("/private-suite")} className="text-white/60 hover:text-white">
            <ArrowLeft size={24} />
          </button>
          <h1 className="text-lg font-heading">Your Journey</h1>
          <button 
            onClick={() => setShowGoalEditor(true)}
            className="text-white/60 hover:text-white"
          >
            <Settings size={20} />
          </button>
        </div>
      </div>

      {/* Summary Cards */}
      <div className="px-6 mb-8">
        <div className="grid grid-cols-2 gap-4">
          {/* Total Minutes */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="bg-gradient-to-br from-violet-500/20 to-purple-500/20 rounded-2xl p-5 border border-white/10"
          >
            <Clock size={20} className="text-violet-400 mb-3" />
            <p className="text-2xl font-semibold text-white">
              {formatMinutes(stats.summary.total_minutes)}
            </p>
            <p className="text-white/50 text-sm">Total Time</p>
          </motion.div>

          {/* Total Sessions */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.05 }}
            className="bg-gradient-to-br from-amber-500/20 to-orange-500/20 rounded-2xl p-5 border border-white/10"
          >
            <Target size={20} className="text-amber-400 mb-3" />
            <p className="text-2xl font-semibold text-white">
              {stats.summary.total_sessions}
            </p>
            <p className="text-white/50 text-sm">Sessions</p>
          </motion.div>

          {/* Current Streak */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.1 }}
            className="bg-gradient-to-br from-orange-500/20 to-red-500/20 rounded-2xl p-5 border border-white/10"
          >
            <Flame size={20} className="text-orange-400 mb-3" />
            <p className="text-2xl font-semibold text-white">
              {stats.summary.current_streak}
            </p>
            <p className="text-white/50 text-sm">Day Streak</p>
          </motion.div>

          {/* This Week */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.15 }}
            className="bg-gradient-to-br from-emerald-500/20 to-teal-500/20 rounded-2xl p-5 border border-white/10"
          >
            <TrendingUp size={20} className="text-emerald-400 mb-3" />
            <p className="text-2xl font-semibold text-white">
              {formatMinutes(stats.summary.this_week_minutes)}
            </p>
            <p className="text-white/50 text-sm">This Week</p>
          </motion.div>
        </div>
      </div>

      {/* Weekly Goals Progress */}
      <div className="px-6 mb-8">
        <h2 className="text-white/70 text-sm font-medium uppercase tracking-wider mb-4">Weekly Goals</h2>
        <Card className="bg-white/5 border-white/10 p-5 space-y-5">
          {/* Minutes Goal */}
          <div>
            <div className="flex justify-between items-center mb-2">
              <span className="text-white/70 text-sm">Minutes</span>
              <span className="text-white text-sm">
                {formatMinutes(stats.goals_progress.weekly_minutes.current)} / {formatMinutes(stats.goals_progress.weekly_minutes.goal)}
              </span>
            </div>
            <div className="h-2 bg-white/10 rounded-full overflow-hidden">
              <motion.div
                initial={{ width: 0 }}
                animate={{ width: `${stats.goals_progress.weekly_minutes.progress}%` }}
                transition={{ duration: 1, ease: "easeOut" }}
                className="h-full bg-gradient-to-r from-violet-500 to-purple-500 rounded-full"
              />
            </div>
          </div>

          {/* Sessions Goal */}
          <div>
            <div className="flex justify-between items-center mb-2">
              <span className="text-white/70 text-sm">Sessions</span>
              <span className="text-white text-sm">
                {stats.goals_progress.weekly_sessions.current} / {stats.goals_progress.weekly_sessions.goal}
              </span>
            </div>
            <div className="h-2 bg-white/10 rounded-full overflow-hidden">
              <motion.div
                initial={{ width: 0 }}
                animate={{ width: `${stats.goals_progress.weekly_sessions.progress}%` }}
                transition={{ duration: 1, ease: "easeOut", delay: 0.1 }}
                className="h-full bg-gradient-to-r from-amber-500 to-orange-500 rounded-full"
              />
            </div>
          </div>

          {/* Streak Goal */}
          <div>
            <div className="flex justify-between items-center mb-2">
              <span className="text-white/70 text-sm">Streak Goal</span>
              <span className="text-white text-sm">
                {stats.goals_progress.streak.current} / {stats.goals_progress.streak.goal} days
              </span>
            </div>
            <div className="h-2 bg-white/10 rounded-full overflow-hidden">
              <motion.div
                initial={{ width: 0 }}
                animate={{ width: `${stats.goals_progress.streak.progress}%` }}
                transition={{ duration: 1, ease: "easeOut", delay: 0.2 }}
                className="h-full bg-gradient-to-r from-orange-500 to-red-500 rounded-full"
              />
            </div>
          </div>
        </Card>
      </div>

      {/* Milestone Progress */}
      {stats.current_milestone && (
        <div className="px-6 mb-8">
          <h2 className="text-white/70 text-sm font-medium uppercase tracking-wider mb-4">Next Milestone</h2>
          <Card className="bg-gradient-to-br from-amber-500/10 to-orange-500/10 border-amber-500/20 p-5">
            <div className="flex items-center gap-4">
              <div className="w-14 h-14 bg-amber-500/20 rounded-full flex items-center justify-center">
                <Award size={24} className="text-amber-400" />
              </div>
              <div className="flex-1">
                <h3 className="text-white font-medium">{stats.current_milestone.name}</h3>
                <p className="text-white/50 text-sm">
                  {formatMinutes(stats.current_milestone.current)} of {formatMinutes(stats.current_milestone.target)}
                </p>
                <div className="h-1.5 bg-white/10 rounded-full mt-2 overflow-hidden">
                  <motion.div
                    initial={{ width: 0 }}
                    animate={{ width: `${stats.current_milestone.progress}%` }}
                    transition={{ duration: 1.5, ease: "easeOut" }}
                    className="h-full bg-gradient-to-r from-amber-400 to-orange-400 rounded-full"
                  />
                </div>
              </div>
            </div>
          </Card>
        </div>
      )}

      {/* Weekly Chart */}
      <div className="px-6 mb-8">
        <h2 className="text-white/70 text-sm font-medium uppercase tracking-wider mb-4">Weekly Activity</h2>
        <Card className="bg-white/5 border-white/10 p-5">
          <div className="flex items-end justify-between h-32 gap-2">
            {stats.weekly_data.slice(-7).map((week, idx) => {
              const maxMins = Math.max(...stats.weekly_data.map(w => w.minutes), 1);
              const height = week.minutes > 0 ? Math.max(8, (week.minutes / maxMins) * 100) : 4;
              const isCurrentWeek = idx === stats.weekly_data.slice(-7).length - 1;
              
              return (
                <div key={week.week} className="flex-1 flex flex-col items-center gap-2">
                  <motion.div
                    initial={{ height: 0 }}
                    animate={{ height: `${height}%` }}
                    transition={{ duration: 0.8, delay: idx * 0.1, ease: "easeOut" }}
                    className={`w-full rounded-t-lg ${
                      isCurrentWeek 
                        ? "bg-gradient-to-t from-violet-500 to-purple-400" 
                        : week.minutes > 0 
                          ? "bg-white/20" 
                          : "bg-white/5"
                    }`}
                  />
                  <span className="text-white/40 text-[10px]">{week.label.split(" ")[0]}</span>
                </div>
              );
            })}
          </div>
        </Card>
      </div>

      {/* Calendar Heatmap */}
      <div className="px-6 mb-8">
        <h2 className="text-white/70 text-sm font-medium uppercase tracking-wider mb-4">
          Practice Calendar
          <span className="text-white/40 ml-2 font-normal normal-case">
            {stats.frequency_stats.practice_percentage_this_month}% this month
          </span>
        </h2>
        <Card className="bg-white/5 border-white/10 p-4 overflow-x-auto">
          <div className="grid grid-cols-13 gap-1 min-w-[300px]">
            {/* Last 91 days in 7 rows (weeks) */}
            {Array.from({ length: 7 }).map((_, weekday) => (
              <div key={weekday} className="contents">
                {stats.calendar_data
                  .filter(d => d.weekday === weekday)
                  .slice(-13)
                  .map((day, idx) => (
                    <div
                      key={day.date}
                      className={`w-4 h-4 rounded-sm ${
                        day.practiced
                          ? "bg-violet-500"
                          : "bg-white/5"
                      }`}
                      title={`${day.date}: ${day.practiced ? "Practiced" : "No practice"}`}
                    />
                  ))}
              </div>
            ))}
          </div>
          <div className="flex items-center justify-end gap-2 mt-3 text-xs text-white/40">
            <span>Less</span>
            <div className="w-3 h-3 rounded-sm bg-white/5" />
            <div className="w-3 h-3 rounded-sm bg-violet-500/40" />
            <div className="w-3 h-3 rounded-sm bg-violet-500" />
            <span>More</span>
          </div>
        </Card>
      </div>

      {/* Mode Breakdown */}
      <div className="px-6 mb-8">
        <h2 className="text-white/70 text-sm font-medium uppercase tracking-wider mb-4">Time by Mode</h2>
        <Card className="bg-white/5 border-white/10 p-5 space-y-4">
          {stats.mode_breakdown.time_by_mode.map((mode, idx) => {
            const Icon = MODE_ICONS[mode.label] || Heart;
            const totalMins = stats.mode_breakdown.time_by_mode.reduce((sum, m) => sum + m.minutes, 0);
            const percentage = totalMins > 0 ? (mode.minutes / totalMins) * 100 : 0;
            const colors = [
              "from-violet-500 to-purple-500",
              "from-orange-500 to-red-500",
              "from-slate-500 to-indigo-500"
            ];
            
            return (
              <div key={mode.mode}>
                <div className="flex items-center justify-between mb-2">
                  <div className="flex items-center gap-3">
                    <Icon size={18} className="text-white/60" />
                    <span className="text-white/80 text-sm">{mode.label}</span>
                  </div>
                  <span className="text-white/50 text-sm">{formatMinutes(mode.minutes)}</span>
                </div>
                <div className="h-2 bg-white/10 rounded-full overflow-hidden">
                  <motion.div
                    initial={{ width: 0 }}
                    animate={{ width: `${percentage}%` }}
                    transition={{ duration: 1, delay: idx * 0.1 }}
                    className={`h-full bg-gradient-to-r ${colors[idx % colors.length]} rounded-full`}
                  />
                </div>
              </div>
            );
          })}
        </Card>
      </div>

      {/* Favorites */}
      <div className="px-6 mb-8">
        <h2 className="text-white/70 text-sm font-medium uppercase tracking-wider mb-4">Your Favorites</h2>
        <div className="space-y-3">
          {/* Focus Areas */}
          {stats.mode_breakdown.favorite_focus_areas.length > 0 && (
            <Card className="bg-white/5 border-white/10 p-4">
              <div className="flex items-center gap-2 mb-3">
                <Target size={16} className="text-violet-400" />
                <span className="text-white/60 text-sm">Focus Areas</span>
              </div>
              <div className="flex flex-wrap gap-2">
                {stats.mode_breakdown.favorite_focus_areas.map((f, idx) => (
                  <span
                    key={f.focus}
                    className={`px-3 py-1.5 rounded-full text-sm ${
                      idx === 0 
                        ? "bg-violet-500/20 text-violet-300 border border-violet-500/30" 
                        : "bg-white/5 text-white/50"
                    }`}
                  >
                    {f.label}
                  </span>
                ))}
              </div>
            </Card>
          )}

          {/* Audio */}
          {stats.mode_breakdown.favorite_atmospheres.length > 0 && (
            <Card className="bg-white/5 border-white/10 p-4">
              <div className="flex items-center gap-2 mb-3">
                <Music size={16} className="text-emerald-400" />
                <span className="text-white/60 text-sm">Ambient Audio</span>
              </div>
              <div className="flex flex-wrap gap-2">
                {stats.mode_breakdown.favorite_atmospheres.map((a, idx) => (
                  <span
                    key={a.atmosphere}
                    className={`px-3 py-1.5 rounded-full text-sm ${
                      idx === 0 
                        ? "bg-emerald-500/20 text-emerald-300 border border-emerald-500/30" 
                        : "bg-white/5 text-white/50"
                    }`}
                  >
                    {a.label}
                  </span>
                ))}
              </div>
            </Card>
          )}

          {/* Brain States */}
          {stats.mode_breakdown.favorite_brain_states.length > 0 && (
            <Card className="bg-white/5 border-white/10 p-4">
              <div className="flex items-center gap-2 mb-3">
                <Brain size={16} className="text-amber-400" />
                <span className="text-white/60 text-sm">Brain States</span>
              </div>
              <div className="flex flex-wrap gap-2">
                {stats.mode_breakdown.favorite_brain_states.map((b, idx) => (
                  <span
                    key={b.brain_state}
                    className={`px-3 py-1.5 rounded-full text-sm ${
                      idx === 0 
                        ? "bg-amber-500/20 text-amber-300 border border-amber-500/30" 
                        : "bg-white/5 text-white/50"
                    }`}
                  >
                    {b.label}
                  </span>
                ))}
              </div>
            </Card>
          )}
        </div>
      </div>

      {/* Personal Bests */}
      <div className="px-6 mb-8">
        <h2 className="text-white/70 text-sm font-medium uppercase tracking-wider mb-4">Personal Bests</h2>
        <Card className="bg-white/5 border-white/10 p-5">
          <div className="space-y-4">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-violet-500/20 rounded-full flex items-center justify-center">
                  <Clock size={18} className="text-violet-400" />
                </div>
                <span className="text-white/70">Longest Session</span>
              </div>
              <span className="text-white font-medium">{formatMinutes(stats.personal_bests.longest_session_minutes)}</span>
            </div>
            
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-orange-500/20 rounded-full flex items-center justify-center">
                  <Flame size={18} className="text-orange-400" />
                </div>
                <span className="text-white/70">Longest Streak</span>
              </div>
              <span className="text-white font-medium">{stats.personal_bests.longest_streak} days</span>
            </div>
            
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-emerald-500/20 rounded-full flex items-center justify-center">
                  <TrendingUp size={18} className="text-emerald-400" />
                </div>
                <span className="text-white/70">Best Week</span>
              </div>
              <span className="text-white font-medium">{formatMinutes(stats.personal_bests.best_week_minutes)}</span>
            </div>
          </div>
        </Card>
      </div>

      {/* Last Session */}
      {stats.last_session && (
        <div className="px-6 mb-8">
          <h2 className="text-white/70 text-sm font-medium uppercase tracking-wider mb-4">Last Session</h2>
          <Card className="bg-white/5 border-white/10 p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-white/80 capitalize">{stats.last_session.mode?.replace("_", " ")}</p>
                <p className="text-white/40 text-sm">
                  {formatMinutes(stats.last_session.duration_minutes)} · {getRelativeTime(stats.last_session.completed_at)}
                </p>
              </div>
              <button
                onClick={() => navigate("/private-suite")}
                className="px-4 py-2 bg-white/10 hover:bg-white/15 rounded-full text-sm text-white/70 flex items-center gap-2"
              >
                New Session <ChevronRight size={16} />
              </button>
            </div>
          </Card>
        </div>
      )}

      {/* Milestones - Shareable Achievement Badges */}
      <div className="px-6 mb-8">
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-white/70 text-sm font-medium uppercase tracking-wider">Achievements</h2>
          {stats.milestones.some(m => m.achieved) && (
            <span className="text-white/40 text-xs">Tap to share</span>
          )}
        </div>
        <div className="space-y-3">
          {stats.milestones.map((milestone) => (
            <AchievementBadge
              key={milestone.target}
              milestone={milestone.target}
              achieved={milestone.achieved}
              totalMinutes={stats.summary.total_minutes}
              onClick={() => {
                setSelectedMilestone(milestone.target);
                setShowAchievementModal(true);
              }}
            />
          ))}
        </div>
      </div>

      {/* Achievement Share Modal */}
      <AchievementModal
        isOpen={showAchievementModal}
        onClose={() => {
          setShowAchievementModal(false);
          setSelectedMilestone(null);
        }}
        milestone={selectedMilestone}
        totalMinutes={stats.summary.total_minutes}
      />

      {/* Goal Editor Modal */}
      <AnimatePresence>
        {showGoalEditor && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/60 backdrop-blur-sm z-50 flex items-end justify-center p-4"
            onClick={() => setShowGoalEditor(false)}
          >
            <motion.div
              initial={{ y: 100, opacity: 0 }}
              animate={{ y: 0, opacity: 1 }}
              exit={{ y: 100, opacity: 0 }}
              onClick={(e) => e.stopPropagation()}
              className="bg-[#1a1a2e] rounded-t-3xl w-full max-w-md p-6 pb-8"
            >
              <div className="w-12 h-1 bg-white/20 rounded-full mx-auto mb-6" />
              <h3 className="text-xl font-heading text-white mb-6">Set Your Goals</h3>
              
              <div className="space-y-6">
                {/* Weekly Minutes */}
                <div>
                  <label className="text-white/60 text-sm mb-2 block">Weekly Minutes Goal</label>
                  <div className="flex items-center gap-3">
                    {[30, 60, 90, 120].map((mins) => (
                      <button
                        key={mins}
                        onClick={() => setGoals({ ...goals, weekly_minutes_goal: mins })}
                        className={`flex-1 py-3 rounded-xl text-sm font-medium transition-all ${
                          goals.weekly_minutes_goal === mins
                            ? "bg-violet-500 text-white"
                            : "bg-white/5 text-white/50 hover:bg-white/10"
                        }`}
                      >
                        {mins}m
                      </button>
                    ))}
                  </div>
                </div>

                {/* Weekly Sessions */}
                <div>
                  <label className="text-white/60 text-sm mb-2 block">Weekly Sessions Goal</label>
                  <div className="flex items-center gap-3">
                    {[3, 5, 7, 10].map((num) => (
                      <button
                        key={num}
                        onClick={() => setGoals({ ...goals, weekly_sessions_goal: num })}
                        className={`flex-1 py-3 rounded-xl text-sm font-medium transition-all ${
                          goals.weekly_sessions_goal === num
                            ? "bg-amber-500 text-white"
                            : "bg-white/5 text-white/50 hover:bg-white/10"
                        }`}
                      >
                        {num}
                      </button>
                    ))}
                  </div>
                </div>

                {/* Streak Goal */}
                <div>
                  <label className="text-white/60 text-sm mb-2 block">Streak Goal (days)</label>
                  <div className="flex items-center gap-3">
                    {[7, 14, 21, 30].map((days) => (
                      <button
                        key={days}
                        onClick={() => setGoals({ ...goals, streak_goal: days })}
                        className={`flex-1 py-3 rounded-xl text-sm font-medium transition-all ${
                          goals.streak_goal === days
                            ? "bg-orange-500 text-white"
                            : "bg-white/5 text-white/50 hover:bg-white/10"
                        }`}
                      >
                        {days}
                      </button>
                    ))}
                  </div>
                </div>
              </div>

              <Button
                onClick={saveGoals}
                disabled={savingGoals}
                className="w-full mt-8 bg-white text-[#1a1a2e] py-4 rounded-full font-medium"
              >
                {savingGoals ? "Saving..." : "Save Goals"}
              </Button>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default PrivateSuiteStatsPage;
